import React, { useState, useMemo } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import {
  Trip,
  WaytoraExperienceInitial,
  WaytoraExperienceListResponse,
} from "@/types";
import { ExperienceItem } from "./experienceItem";
import { SkeletonLoaderItem } from "./experienceUtils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useAuthStore from "@/stores/user/authStore";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import useExperiencesStore from "@/stores/tripBuilder/useExperiences";

// Define items per page constant
export const EXPERIENCES_PER_PAGE = 12;

// COMPONENTS - ExperienceList

interface ExperienceListProps {
  updateTripData: (tripData: Trip) => void;
  status: string;
  isWaytoraFetching: boolean;
  waytoraData: WaytoraExperienceListResponse;
  waytoraError: any;
  setSelectedExperience: (place: any) => void;
  handleOpenModal: (placeId: string) => void;
  handleLoadMore: (type: "viator" | "waytora") => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const ExperienceList: React.FC<ExperienceListProps> = ({
  updateTripData,
  status,
  isWaytoraFetching,
  waytoraData,
  waytoraError,
  setSelectedExperience,
  handleOpenModal,
  handleLoadMore,
  currentPage,
  setCurrentPage,
}) => {
  const tripData = useTripStore((state) => state.tripData);
  const selectedDay = useTripStore((state) => state.selectedDay);
  const selectedLocation = useTripStore((state) => state.selectedLocation);
  const checkIsAdmin = useTripStore((state) => state.checkIsAdmin);
  const addExperienceToItinerary = useTripStore(
    (state) => state.addExperienceToItinerary
  );

  // experience store
  const addExperienceToDay = useExperiencesStore(
    (state) => state.addExperienceToDay
  );
  const setAddingExperience = useExperiencesStore(
    (state) => state.setAddingExperience
  );
  const { anonymousUser } = useAuthStore();
  const { setRateLimit } = useRateLimitStore();
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);
  const [isAddingExperience, setIsAddingExperience] = useState<string | null>(
    null
  );

  const handleAddToDayClick = async (experience: WaytoraExperienceInitial) => {
    if (!selectedDay || !selectedLocation || !isAdmin) return;

    setIsAddingExperience(experience.id.toString());
    addExperienceToDay(experience);
    try {
      await addExperienceToItinerary(
        experience,
        selectedDay,
        selectedLocation.id
      );
    } catch (error) {
      console.error("Error calling addExperienceToItinerary from list:", error);
    } finally {
      setIsAddingExperience(null);
      setAddingExperience(null);
    }
  };

  const handleLike = async (experienceId: string) => {
    if (!tripData) return;
    const newLikedState = !tripData.liked_experiences.includes(experienceId);
    updateTripData({
      ...tripData,
      liked_experiences: newLikedState
        ? [...tripData.liked_experiences, experienceId]
        : tripData.liked_experiences.filter((id) => id !== experienceId),
    });
  };

  const handleNextPage = () => {
    if (anonymousUser) {
      setRateLimit("Create your account to view more experiences!");
      return;
    }
    if (waytoraData?.nextCursor !== null) {
      handleLoadMore("waytora");
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <React.Fragment>
      {status === "pending" ? (
        Array.from({ length: 6 }, (_, i) => i).map((_, index) => (
          <SkeletonLoaderItem key={index} />
        ))
      ) : status === "error" ? (
        <p>Error: {waytoraError?.message}</p>
      ) : (
        <>
          {waytoraData?.data.map(
            (experience: WaytoraExperienceInitial, i: number) => (
              <React.Fragment key={i}>
                <ExperienceItem
                  key={experience.id}
                  experienceData={experience}
                  onOpenModal={handleOpenModal}
                  setSelectedExperience={setSelectedExperience}
                  addToItinerary={handleAddToDayClick}
                  isFetchingDetails={
                    isAddingExperience === experience.id.toString()
                  }
                  isLiked={
                    tripData?.liked_experiences?.includes(
                      experience.waytora_place_id
                    ) ?? false
                  }
                  setIsLiked={handleLike}
                  isAdmin={isAdmin}
                />
              </React.Fragment>
            )
          )}
          {isWaytoraFetching &&
            Array.from({ length: 4 }, (_, i) => i).map((_, index) => (
              <SkeletonLoaderItem key={`next-page-${index}`} />
            ))}

          {waytoraData?.data && waytoraData.data.length > 0 && (
            <div className="flex justify-center items-center gap-4 py-4 mt-2 bg-white border-t w-full col-span-full">
              <Button
                variant="outline"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-neutral-500 font-light px-2">
                Page {currentPage + 1}
              </span>
              <Button
                variant="outline"
                onClick={handleNextPage}
                disabled={
                  !waytoraData?.data ||
                  waytoraData.data.length === 0 ||
                  !waytoraData.data[waytoraData.data.length - 1] ||
                  waytoraData.nextCursor === null
                }
                className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default ExperienceList;
